import { useMutation } from "@apollo/client";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  InternalAdminUsersSetActiveMutation,
  InternalAdminUsersSetActiveMutationVariables,
} from "gql/graphql";
import Toast from "modules/toasts/toast";
import React, { memo, useCallback } from "react";
import colors from "app/colors";
import { WarningIcon, TrashOutlineIcon } from "modules/icons";
import INTERNAL_ADMIN_USERS_SET_ACTIVE_MUTATION from "../_networking/internal-admin-users-set-active";
import useQueryString from "modules/hooks/use-query-string";
import useDisplayToast from "modules/hooks/use-display-toast";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  userIds: string[];
}

const InternalAdminDeactivateMultipleUsersModal = ({ userIds }: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();
  const { displayToast } = useDisplayToast();
  const [internalAdminUsersSetActiveAPI] = useMutation<
    InternalAdminUsersSetActiveMutation,
    InternalAdminUsersSetActiveMutationVariables
  >(INTERNAL_ADMIN_USERS_SET_ACTIVE_MUTATION);

  const onDeactivateUserClicked = useCallback(() => {
    if (!userIds.length || !qsOrganizationId) return;

    internalAdminUsersSetActiveAPI({
      variables: {
        organizationId: qsOrganizationId,
        idList: userIds,
        active: false,
      },
    });
    displayToast(() => (
      <Toast
        message={`${userIds.length} users deactivated`}
        leftIcon={<TrashOutlineIcon boxSize={5} fill={colors.grayMain} />}
      />
    ));
    closeModal();
  }, [
    internalAdminUsersSetActiveAPI,
    closeModal,
    qsOrganizationId,
    displayToast,
    userIds,
  ]);

  return (
    <Modal isOpen onClose={closeModal} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <VStack>
            <WarningIcon boxSize={140} />
            <Text fontSize={20} fontWeight={700}>
              Deactivate User?
            </Text>
            <Text>
              Are you sure you want to deactivate {userIds.length} users?
            </Text>
            <HStack>
              <Button variant="cancel" height={8} w={28} onClick={closeModal}>
                No
              </Button>
              <Button height={8} w={28} onClick={onDeactivateUserClicked}>
                Yes
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminDeactivateMultipleUsersModal);
